import React, { useState, useEffect } from 'react'
import {
  Segment,
  Header,
  Table,
  Icon,
  Label,
  Button,
  Dropdown,
  Input,
  Message,
  Accordion,
  Grid,
  Statistic,
  Progress,
  Loader,
  Dimmer,
  Modal,
  Form,
} from 'semantic-ui-react'
import { toast } from 'react-toastify'
import useAxios from '../lib/auth/useAxios'

const MaterialProductMatch = ({ material, onUpdate }) => {
  const axios = useAxios()
  const [matches, setMatches] = useState([])
  const [loading, setLoading] = useState(false)
  const [showMatchModal, setShowMatchModal] = useState(false)

  const loadMatches = async () => {
    setLoading(true)
    try {
      const response = await axios.get('/main/takeoff-product-matches/', {
        params: { takeoff_material: material.id },
      })
      setMatches(response.data.results || [])
    } catch (error) {
      console.error('Error loading matches:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (material.id) {
      loadMatches()
    }
  }, [material.id])

  const handleApproveMatch = async (matchId) => {
    try {
      await axios.patch(`/main/takeoff-product-matches/${matchId}/`, {
        status: 'approved',
      })
      toast.success('Product match approved')
      loadMatches()
      onUpdate()
    } catch (error) {
      console.error('Error approving match:', error)
      toast.error('Failed to approve match')
    }
  }

  return (
    <>
      <Table.Cell>
        {material.is_matched ? (
          <>
            {matches.length > 0 && (
              <Label color="green" size="small">
                <Icon name="check" />
                {matches[0].product_name || 'Matched'}
              </Label>
            )}
          </>
        ) : (
          <Button
            size="mini"
            icon="search"
            content="Find Matches"
            onClick={() => setShowMatchModal(true)}
            basic
          />
        )}
      </Table.Cell>
      <Table.Cell>
        {material.unit_price ? (
          `$${material.unit_price.toFixed(2)}`
        ) : (
          <span style={{ color: '#999' }}>-</span>
        )}
      </Table.Cell>
      <Table.Cell>
        {material.total_price ? (
          <strong>${material.total_price.toFixed(2)}</strong>
        ) : (
          <span style={{ color: '#999' }}>-</span>
        )}
      </Table.Cell>

      <Modal
        open={showMatchModal}
        onClose={() => setShowMatchModal(false)}
        size="large"
      >
        <Modal.Header>
          Find Product Matches for {material.material_name}
        </Modal.Header>
        <Modal.Content>
          {loading ? (
            <Loader active inline="centered">
              Searching for matches...
            </Loader>
          ) : (
            <Table celled>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Product</Table.HeaderCell>
                  <Table.HeaderCell>Match Type</Table.HeaderCell>
                  <Table.HeaderCell>Confidence</Table.HeaderCell>
                  <Table.HeaderCell>Price</Table.HeaderCell>
                  <Table.HeaderCell>Actions</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {matches.map((match) => (
                  <Table.Row key={match.id}>
                    <Table.Cell>{match.product_name}</Table.Cell>
                    <Table.Cell>
                      <Label size="small">
                        {match.match_type}
                      </Label>
                    </Table.Cell>
                    <Table.Cell>
                      <Progress
                        percent={match.match_confidence * 100}
                        size="tiny"
                        color={match.match_confidence > 0.8 ? 'green' : 'yellow'}
                      />
                    </Table.Cell>
                    <Table.Cell>${match.unit_price || 'N/A'}</Table.Cell>
                    <Table.Cell>
                      <Button
                        size="mini"
                        color="green"
                        onClick={() => handleApproveMatch(match.id)}
                      >
                        Approve
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowMatchModal(false)}>Close</Button>
        </Modal.Actions>
      </Modal>
    </>
  )
}

const MaterialsList = ({ takeoffId, primaryColor, secondaryColor }) => {
  const axios = useAxios()
  const [loading, setLoading] = useState(true)
  const [materials, setMaterials] = useState([])
  const [materialsList, setMaterialsList] = useState(null)
  const [activeCategory, setActiveCategory] = useState(-1)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')

  useEffect(() => {
    if (takeoffId) {
      loadMaterialsData()
    }
  }, [takeoffId])

  const loadMaterialsData = async () => {
    setLoading(true)
    try {
      // Load materials
      const materialsResponse = await axios.get('/main/takeoff-materials/', {
        params: { takeoff: takeoffId },
      })
      setMaterials(materialsResponse.data.results || [])

      // Load materials list summary
      const listResponse = await axios.get('/main/takeoff-materials-lists/', {
        params: { takeoff: takeoffId },
      })
      if (listResponse.data.results && listResponse.data.results.length > 0) {
        setMaterialsList(listResponse.data.results[0])
      }
    } catch (error) {
      console.error('Error loading materials:', error)
      toast.error('Failed to load materials data')
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateTotals = async () => {
    try {
      await axios.post(`/main/takeoff-materials-lists/${materialsList.id}/update_totals/`)
      toast.success('Totals updated')
      loadMaterialsData()
    } catch (error) {
      console.error('Error updating totals:', error)
      toast.error('Failed to update totals')
    }
  }

  const handleExportMaterials = () => {
    toast.info('Export functionality coming soon')
  }

  // Group materials by category
  const groupedMaterials = materials.reduce((acc, material) => {
    const category = material.material_category || 'Uncategorized'
    if (!acc[category]) {
      acc[category] = []
    }
    acc[category].push(material)
    return acc
  }, {})

  // Filter materials
  const filteredMaterials = materials.filter((material) => {
    const matchesSearch = material.material_name
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
    const matchesStatus =
      filterStatus === 'all' ||
      (filterStatus === 'matched' && material.is_matched) ||
      (filterStatus === 'unmatched' && !material.is_matched)
    return matchesSearch && matchesStatus
  })

  const categoryPanels = Object.entries(groupedMaterials).map(
    ([category, categoryMaterials], index) => ({
      key: index,
      title: {
        content: (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>
              <Icon name="folder" />
              {category}
            </span>
            <Label circular size="small">
              {categoryMaterials.length}
            </Label>
          </div>
        ),
      },
      content: {
        content: (
          <Table celled striped>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell width={6}>Material</Table.HeaderCell>
                <Table.HeaderCell width={2}>Quantity</Table.HeaderCell>
                <Table.HeaderCell width={2}>Unit</Table.HeaderCell>
                <Table.HeaderCell width={3}>Product Match</Table.HeaderCell>
                <Table.HeaderCell width={2}>Unit Price</Table.HeaderCell>
                <Table.HeaderCell width={2}>Total</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {categoryMaterials
                .filter((m) => filteredMaterials.includes(m))
                .map((material) => (
                  <Table.Row key={material.id}>
                    <Table.Cell>
                      <strong>{material.material_name}</strong>
                      {material.material_type && (
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          Type: {material.material_type}
                        </div>
                      )}
                      {material.specifications &&
                        Object.keys(material.specifications).length > 0 && (
                          <div style={{ fontSize: '11px', color: '#888' }}>
                            {Object.entries(material.specifications)
                              .map(([key, value]) => `${key}: ${value}`)
                              .join(', ')}
                          </div>
                        )}
                    </Table.Cell>
                    <Table.Cell>
                      <strong>{material.quantity_required}</strong>
                    </Table.Cell>
                    <Table.Cell>{material.unit_of_measure}</Table.Cell>
                    <MaterialProductMatch
                      material={material}
                      onUpdate={loadMaterialsData}
                    />
                  </Table.Row>
                ))}
            </Table.Body>
          </Table>
        ),
      },
    })
  )

  if (loading) {
    return (
      <Segment style={{ height: '100%' }}>
        <Dimmer active inverted>
          <Loader inverted>Loading Materials...</Loader>
        </Dimmer>
      </Segment>
    )
  }

  return (
    <div style={{ height: '100%', overflow: 'auto', padding: '20px' }}>
      {/* Summary Statistics */}
      <Segment>
        <Grid columns={4} divided textAlign="center">
          <Grid.Column>
            <Statistic size="tiny">
              <Statistic.Value>{materials.length}</Statistic.Value>
              <Statistic.Label>Total Items</Statistic.Label>
            </Statistic>
          </Grid.Column>
          <Grid.Column>
            <Statistic size="tiny" color="green">
              <Statistic.Value>
                {materials.filter((m) => m.is_matched).length}
              </Statistic.Value>
              <Statistic.Label>Matched</Statistic.Label>
            </Statistic>
          </Grid.Column>
          <Grid.Column>
            <Statistic size="tiny">
              <Statistic.Value>
                {materialsList?.total_estimated_cost ? (
                  `$${materialsList.total_estimated_cost.toFixed(0)}`
                ) : (
                  '-'
                )}
              </Statistic.Value>
              <Statistic.Label>Estimated Cost</Statistic.Label>
            </Statistic>
          </Grid.Column>
          <Grid.Column>
            <Statistic size="tiny" color="blue">
              <Statistic.Value>
                {materialsList?.total_confirmed_cost ? (
                  `$${materialsList.total_confirmed_cost.toFixed(0)}`
                ) : (
                  '-'
                )}
              </Statistic.Value>
              <Statistic.Label>Confirmed Cost</Statistic.Label>
            </Statistic>
          </Grid.Column>
        </Grid>
      </Segment>

      {/* Controls */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
        }}
      >
        <div style={{ display: 'flex', gap: '10px' }}>
          <Input
            icon="search"
            placeholder="Search materials..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Dropdown
            selection
            value={filterStatus}
            onChange={(e, { value }) => setFilterStatus(value)}
            options={[
              { key: 'all', text: 'All Materials', value: 'all' },
              { key: 'matched', text: 'Matched Only', value: 'matched' },
              { key: 'unmatched', text: 'Unmatched Only', value: 'unmatched' },
            ]}
          />
        </div>
        <Button.Group>
          <Button icon="refresh" content="Update Totals" onClick={handleUpdateTotals} />
          <Button
            icon="download"
            content="Export"
            onClick={handleExportMaterials}
            primary
          />
        </Button.Group>
      </div>

      {/* Materials by Category */}
      {materials.length === 0 ? (
        <Message info>
          <Message.Header>No Materials Found</Message.Header>
          <p>
            Run takeoff actions to extract materials from your construction documents.
          </p>
        </Message>
      ) : (
        <Accordion
          panels={categoryPanels}
          activeIndex={activeCategory}
          onTitleClick={(e, { index }) => 
            setActiveCategory(activeCategory === index ? -1 : index)
          }
          styled
          fluid
        />
      )}

      {/* Status Bar */}
      {materialsList && (
        <Segment attached="bottom" secondary>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Label
                color={
                  materialsList.status === 'approved'
                    ? 'green'
                    : materialsList.status === 'in_review'
                    ? 'yellow'
                    : 'grey'
                }
              >
                {materialsList.status.replace('_', ' ').toUpperCase()}
              </Label>
              <span style={{ marginLeft: '10px', color: '#666' }}>
                Last updated: {new Date(materialsList.updated_at).toLocaleString()}
              </span>
            </div>
            <div>
              <Progress
                percent={(materials.filter((m) => m.is_matched).length / materials.length) * 100}
                size="tiny"
                color="green"
                style={{ marginBottom: 0, minWidth: '200px' }}
              >
                Matching Progress
              </Progress>
            </div>
          </div>
        </Segment>
      )}
    </div>
  )
}

export default MaterialsList